import { create } from 'zustand';
import api, { buildQuery } from '../lib/api';

// Workforce module store — gruppi, festività, ferie, presenze, saldi, costo,
// CCNL rules, accrual jobs. One store for the whole module: the sections share
// the users list and the gruppi list, so centralising avoids refetch churn.
// Actions return the payload so pages can drive optimistic/pessimistic UI
// themselves (optimistic on ferie request, pessimistic on cost/rule edits).

const unwrap = (resp) => (resp && resp.data !== undefined ? resp.data : resp);

let selfSessionInFlight = null;
const notifyPresenceChanged = () => {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('wfm-presence-session-changed'));
  }
};

export const useWorkforceStore = create((set, get) => ({
  utenti: [],
  gruppi: [],
  ccnl: [],
  miaSessione: null,
  miaSessionePronta: false,
  loading: {},

  // Gli elenchi qui dentro dipendono da chi è collegato: l'ambito di
  // visibilità di un ADMIN non è quello di un SUPER_ADMIN. Al cambio di
  // identità si buttano via — vedi lib/datiDiSessione.js.
  azzera: () => {
    selfSessionInFlight = null;
    set({ utenti: [], gruppi: [], ccnl: [], miaSessione: null, miaSessionePronta: false, loading: {} });
  },

  _setLoading: (key, val) =>
    set((s) => ({ loading: { ...s.loading, [key]: val } })),

  // ─── Shared lookups ───────────────────────────────────────────────────
  // Workforce roster: active users + those not yet activated (primo_accesso),
  // so new hires can be assigned to gruppi / workforce before their first login.
  loadUtenti: async () => {
    const resp = await api.get('/users?stato=attivo,primo_accesso&limit=500');
    const utenti = unwrap(resp) || [];
    set({ utenti });
    return utenti;
  },

  // Users the current actor may see in presence views (ADMIN scoped to their
  // gruppo/modulo; MANAGER+ everyone). Returns the list — does NOT overwrite
  // `utenti`, which other workforce pages need unscoped (e.g. adding members).
  loadUtentiVisibili: async () => unwrap(await api.get('/workforce/utenti-visibili')) || [],

  loadGruppi: async () => {
    get()._setLoading('gruppi', true);
    try {
      const resp = await api.get('/workforce/gruppi');
      const gruppi = unwrap(resp) || [];
      set({ gruppi });
      return gruppi;
    } finally {
      get()._setLoading('gruppi', false);
    }
  },

  createGruppo: async (payload) => {
    const resp = await api.post('/workforce/gruppi', payload);
    await get().loadGruppi();
    return unwrap(resp);
  },
  updateGruppo: async (id, payload) => {
    const resp = await api.patch(`/workforce/gruppi/${id}`, payload);
    await get().loadGruppi();
    return unwrap(resp);
  },
  deleteGruppo: async (id) => {
    await api.delete(`/workforce/gruppi/${id}`);
    await get().loadGruppi();
  },

  loadMembri: async (gruppoId) => {
    const resp = await api.get(`/workforce/gruppi/${gruppoId}/membri`);
    return unwrap(resp) || [];
  },
  // Orario di Lavoro summary for a user — drives the membership form's
  // schedule-derived daily-quota preview.
  getOrario: async (accountId, at) =>
    unwrap(await api.get(`/workforce/utenti/${accountId}/orario${buildQuery({ at })}`)),
  addMembro: async (gruppoId, payload) => {
    const resp = await api.post(`/workforce/gruppi/${gruppoId}/membri`, payload);
    return unwrap(resp);
  },
  // Per id: la riga scelta, anche una membership conclusa (solo SUPER_ADMIN).
  updateMembership: async (gruppoId, membershipId, payload) =>
    unwrap(await api.patch(`/workforce/gruppi/${gruppoId}/membership/${membershipId}`, payload)),
  removeMembro: async (gruppoId, accountId) => {
    await api.delete(`/workforce/gruppi/${gruppoId}/membri/${accountId}`);
  },

  // ─── Festività ────────────────────────────────────────────────────────
  loadFestivita: async (params = {}) => {
    const resp = await api.get(`/workforce/festivita${buildQuery(params)}`);
    return unwrap(resp) || [];
  },
  createFestivita: async (payload) => unwrap(await api.post('/workforce/festivita', payload)),
  updateFestivita: async (id, payload) => unwrap(await api.patch(`/workforce/festivita/${id}`, payload)),
  deleteFestivita: async (id) => { await api.delete(`/workforce/festivita/${id}`); },
  importIcs: async (icsBase64) => unwrap(await api.post('/workforce/festivita/import-ics', { ics_base64: icsBase64 })),
  presetNazionale: async (anno) =>
    unwrap(await api.post(`/workforce/festivita/preset-nazionale-it?anno=${anno}`, {})),

  // ─── Ferie ────────────────────────────────────────────────────────────
  loadFerie: async (params = {}) => {
    const resp = await api.get(`/workforce/ferie${buildQuery(params)}`);
    return unwrap(resp) || [];
  },
  createFerie: async (payload) => unwrap(await api.post('/workforce/ferie', payload)),
  approvaFerie: async (id, body = {}) => unwrap(await api.post(`/workforce/ferie/${id}/approva`, body)),
  rifiutaFerie: async (id, body = {}) => unwrap(await api.post(`/workforce/ferie/${id}/rifiuta`, body)),
  annullaFerie: async (id) => unwrap(await api.post(`/workforce/ferie/${id}/annulla`, {})),

  // ─── Registri presenze ────────────────────────────────────────────────
  loadRegistri: async (params = {}) => {
    const resp = await api.get(`/workforce/registri${buildQuery(params)}`);
    return unwrap(resp) || [];
  },
  createRegistro: async (payload) => unwrap(await api.post('/workforce/registri', payload)),
  updateRegistro: async (id, payload) => unwrap(await api.patch(`/workforce/registri/${id}`, payload)),
  deleteRegistro: async (id) => { await api.delete(`/workforce/registri/${id}`); },
  // Ore eccedenti: a day over its schedule is flagged 'pending' and can be
  // confirmed ('approvato') or rejected with a mandatory reason ('rifiutato')
  // directly from Presenze; the closed ISO week is approved with a destination
  // (pagato | banca_ore) from Banca ore. ADMIN+ on the server.
  approvaStraordinari: async (id, nota) => unwrap(await api.post(`/workforce/registri/${id}/straordinari/approva`, nota ? { nota } : {})),
  rifiutaStraordinari: async (id, motivo) => unwrap(await api.post(`/workforce/registri/${id}/straordinari/rifiuta`, { motivo })),
  loadSettimaneStraordinari: async (params) => unwrap(await api.get(`/workforce/straordinari/settimane${buildQuery(params)}`)) || [],
  approvaSettimanaStraordinari: async (payload) => unwrap(await api.post('/workforce/straordinari/settimane/approva', payload)),
  // SUPER_ADMIN tolerance window for clock-in/out (minutes past the scheduled
  // day before a punch is refused). Read is open; write is SUPER_ADMIN.
  getParametriPresenze: async () => unwrap(await api.get('/workforce/parametri-presenze')),
  updateParametriPresenze: async (payload) => unwrap(await api.put('/workforce/parametri-presenze', payload)),
  // FEAT-05: re-read the extra hours already calculated with today's soglia (open weeks only).
  riclassificaStraordinari: async (applica = false) => unwrap(await api.post('/workforce/straordinari/riclassifica', { applica })),
  // Team presence snapshot for one day (ADMIN+): { data, gruppi, users, totale, presenti, inServizio, inPausa }.
  loadPresenzeOverview: async (data) => unwrap(await api.get(`/workforce/presenze/overview${buildQuery({ data })}`)),

  // ─── Timbratura rapida: open clock-in session ──────────────────────────
  // accountId is optional: omit for self (Home PunchClock & TopBar PresenceStatusBadge),
  // pass to act on another user (ADMIN+, from /workforce/presenze). Server enforces authz.
  getSessione: async (accountId) => {
    if (accountId) {
      return unwrap(await api.get(`/workforce/presenze/sessione${buildQuery({ account_id: accountId })}`));
    }
    if (selfSessionInFlight) return selfSessionInFlight;
    selfSessionInFlight = api
      .get('/workforce/presenze/sessione')
      .then((resp) => {
        const sess = unwrap(resp) || null;
        set({ miaSessione: sess, miaSessionePronta: true });
        return sess;
      })
      .catch((err) => {
        set({ miaSessione: null, miaSessionePronta: true });
        throw err;
      })
      .finally(() => {
        selfSessionInFlight = null;
      });
    return selfSessionInFlight;
  },
  sessioneEntrata: async (payload) => {
    const sess = unwrap(await api.post('/workforce/presenze/sessione/entrata', payload));
    if (!payload?.account_id) {
      set({ miaSessione: sess || null, miaSessionePronta: true });
    }
    notifyPresenceChanged();
    return sess;
  },
  sessionePausa: async (ora, accountId) => {
    const sess = unwrap(await api.post('/workforce/presenze/sessione/pausa', { ora, account_id: accountId }));
    if (!accountId) {
      set({ miaSessione: sess || null, miaSessionePronta: true });
    }
    notifyPresenceChanged();
    return sess;
  },
  sessioneUscita: async (payload) => {
    const res = unwrap(await api.post('/workforce/presenze/sessione/uscita', payload));
    if (!payload?.account_id) {
      set({ miaSessione: null, miaSessionePronta: true });
    }
    notifyPresenceChanged();
    return res;
  },
  // SUPER_ADMIN force-terminate (discard) an open session — does not record hours.
  terminaSessione: async (accountId) => {
    await api.delete(`/workforce/presenze/sessione${buildQuery({ account_id: accountId })}`);
    if (!accountId) {
      set({ miaSessione: null, miaSessionePronta: true });
    }
    notifyPresenceChanged();
  },
  // ADMIN+ close an open session with caller-provided values, including
  // shifts that crossed midnight (server splits at the 23:59 boundary).
  // Payload: { account_id, data_inizio, ora_inizio, data_fine, ora_fine,
  //           pausa_minuti?, gruppo_id?, note? }.
  chiudiSessioneAdmin: async (payload) => {
    const res = unwrap(await api.post('/workforce/presenze/sessione/chiudi-admin', payload));
    notifyPresenceChanged();
    return res;
  },

  // ─── Costo / saldi / conflitti ────────────────────────────────────────
  getCosto: async (accountId, at) =>
    unwrap(await api.get(`/workforce/utenti/${accountId}/costo${buildQuery({ at })}`)),
  getCostoLavoro: async (accountId, dal, al) =>
    unwrap(await api.get(`/workforce/utenti/${accountId}/costo-lavoro${buildQuery({ dal, al })}`)),
  getConflitti: async (accountId) =>
    unwrap(await api.get(`/workforce/utenti/${accountId}/conflitti`)),
  getSaldi: async (accountId) =>
    unwrap(await api.get(`/workforce/utenti/${accountId}/saldi`)),

  // ─── CCNL rules ───────────────────────────────────────────────────────
  loadCcnl: async () => {
    const resp = await api.get('/workforce/ccnl');
    const ccnl = unwrap(resp) || [];
    set({ ccnl });
    return ccnl;
  },
  // ─── Profili CCNL ─────────────────────────────────────────────────────
  // Il dato da cui dipendono maturazioni, ROL, ex-festività e banca ore.
  loadProfiliCcnl: async () => unwrap(await api.get('/workforce/profili')) || [],
  salvaProfiloCcnl: async (accountId, campi) =>
    unwrap(await api.put(`/workforce/profili/${accountId}`, campi)),
  applicaProfiliCcnl: async (payload) =>
    unwrap(await api.post('/workforce/profili/massivo', payload)),

  loadRegole: async (ccnlId, at) => {
    const resp = await api.get(`/workforce/ccnl/regole${buildQuery({ ccnl_id: ccnlId, at })}`);
    return unwrap(resp) || [];
  },
  nuovaVersioneRegole: async (payload) =>
    unwrap(await api.post('/workforce/ccnl/regole/nuova-versione', payload)),

  // ─── Accrual jobs ─────────────────────────────────────────────────────
  loadMaturazioni: async () => {
    const resp = await api.get('/workforce/maturazioni');
    return unwrap(resp) || [];
  },
  // Stato del calcolo automatico: mesi mancanti e, soprattutto, se c'è
  // qualcuno per cui maturare. Senza profili CCNL il lavoro gira ogni mese e
  // non scrive niente — e finora nessuno lo veniva a sapere.
  loadStatoMaturazioni: async () => unwrap(await api.get('/workforce/maturazioni/stato')),

  eseguiMaturazione: async (anno, mese) =>
    unwrap(await api.post(`/workforce/maturazioni/esegui?anno=${anno}&mese=${mese}`, {})),

  // ─── Banca ore ────────────────────────────────────────────────────────
  getBancaOre: async (accountId, dal, al) =>
    unwrap(await api.get(`/workforce/utenti/${accountId}/banca-ore${buildQuery({ dal, al })}`)),
  rilevaStraordinari: async (anno, mese) =>
    unwrap(await api.post(`/workforce/straordinari/rileva?anno=${anno}&mese=${mese}`, {})),

  // ─── Blocchi operativi ────────────────────────────────────────────────
  // Time lost to causes outside the worker's control. Counted for statistics;
  // never subtracted from anybody's hours.
  loadCauseBlocco: async (tutte = false) => {
    const resp = await api.get(`/workforce/cause-blocco${tutte ? '?tutte=1' : ''}`);
    return unwrap(resp) || [];
  },
  createCausaBlocco: async (payload) => unwrap(await api.post('/workforce/cause-blocco', payload)),
  updateCausaBlocco: async (id, payload) => unwrap(await api.patch(`/workforce/cause-blocco/${id}`, payload)),
  deleteCausaBlocco: async (id) => unwrap(await api.delete(`/workforce/cause-blocco/${id}`)),

  loadBlocchi: async (params = {}) => {
    const resp = await api.get(`/workforce/blocchi${buildQuery(params)}`);
    return unwrap(resp) || [];
  },
  loadCodaBlocchi: async () => {
    const resp = await api.get('/workforce/blocchi/coda');
    return unwrap(resp) || [];
  },
  loadStatisticheBlocchi: async (params = {}) => {
    const resp = await api.get(`/workforce/blocchi/statistiche${buildQuery(params)}`);
    return unwrap(resp) || null;
  },
  createBlocco: async (payload) => unwrap(await api.post('/workforce/blocchi', payload)),
  createBloccoCollettivo: async (payload) => unwrap(await api.post('/workforce/blocchi/collettivo', payload)),
  updateBlocco: async (id, payload) => unwrap(await api.patch(`/workforce/blocchi/${id}`, payload)),
  deleteBlocco: async (id) => { await api.delete(`/workforce/blocchi/${id}`); },
  approvaBlocco: async (id, body = {}) => unwrap(await api.post(`/workforce/blocchi/${id}/approva`, body)),
  rifiutaBlocco: async (id, body = {}) => unwrap(await api.post(`/workforce/blocchi/${id}/rifiuta`, body)),

  // Blocco in corso: opened from "Problemi in corso", closed by "Problema
  // risolto". Kept server-side so a responsabile can see who is stopped now.
  getBloccoAperto: async () => unwrap(await api.get('/workforce/blocchi/aperto')),
  loadBlocchiAperti: async () => {
    const resp = await api.get('/workforce/blocchi/aperti');
    return unwrap(resp) || [];
  },
  apriBlocco: async (payload) => unwrap(await api.post('/workforce/blocchi/apri', payload)),
  chiudiBlocco: async (payload) => unwrap(await api.post('/workforce/blocchi/chiudi', payload)),
  annullaBloccoAperto: async () => { await api.delete('/workforce/blocchi/aperto'); },
}));
